// constants/collectionIssue.js
//
// Front-of-house constants for the member-answerable collection-issue feature.
// The enum string values MIRROR the backend (App\Enums\Commerce\IssueReason +
// MemberIssueResponse) — do NOT invent values; the server rejects anything not in
// `allowedResponses`. Kept as a small, testable map so components stay declarative.

export const ISSUE_REASON = {
  VARIANT_UNAVAILABLE: 'variant_unavailable',
  QUALITY_CONCERN: 'quality_concern',
  SUPPLIER_OOS: 'supplier_oos',
  PRICE_CHANGE: 'price_change',
  SHORTFALL: 'shortfall',
  OTHER: 'other',
};

// Three answers, and only three (App\Enums\Commerce\MemberIssueResponse, BE→FE
// brief 2026-08-10). Every question a leader can ask reduces to the same
// decision: go ahead, don't, or wait for next time.
//
// The six-case taxonomy this replaced collapsed cleanly: `accept` and `delegate`
// drove byte-identical code, while `choose_alternative` and `suggest_supplier`
// differed only in WHICH field carried the member's pick. WHAT she is agreeing
// to is not a separate answer — it is `option_index`, pointing at one of the
// options the leader offered.
//
// YES and NO are always offered; WAIT only when the leader opted in per
// question. Never assume the set — render `allowedResponses` verbatim.
export const MEMBER_RESPONSE = {
  YES: 'yes',
  NO: 'no',
  WAIT: 'wait',
};

// ItemIssue.status (App\Enums\Commerce\ItemIssueStatus) — the issue header's
// lifecycle, NOT the item's numeric ItemStatus. Since interaction-v2 the member
// may READ an issue in every one of these states (`show` no longer 403s once the
// issue leaves awaiting_member), so the FE must branch on it rather than assume
// "if I could fetch it, it's answerable":
//   AWAITING_MEMBER → the member's court; answerable (first answer).
//   OPEN            → the leader's court. Answerable ONLY as a REVISION, i.e.
//                     when the member already answered (memberResponse != null)
//                     and the leader hasn't applied it yet. OPEN with no answer
//                     means "raised, not sent" — read-only for the member.
//   RESOLVED        → terminal. Read-only; a different answer 409s.
export const ISSUE_STATUS = {
  OPEN: 'open',
  AWAITING_MEMBER: 'awaiting_member',
  RESOLVED: 'resolved',
};

/**
 * Whose court an issue is in, as a TurnPill variant. Shared by the detail screen
 * and the history list so one issue never reads as two different states.
 *
 * A revision-in-progress issue resolves to 'handled' on purpose: once the member
 * has answered, the ball IS with the leader — the answer stays editable only
 * because the leader hasn't applied it yet.
 *
 * @param {{ status?: string }|null|undefined} issue
 */
export function issuePillVariant(issue) {
  const status = issue?.status ?? null;
  if (status === ISSUE_STATUS.RESOLVED) return 'resolved';
  if (status === ISSUE_STATUS.AWAITING_MEMBER) return 'member';
  return 'handled';
}

/**
 * Is this issue answerable (a first answer OR a revision) by the member?
 * The SSOT for "show the response buttons" — mirrors the backend gate
 * (ItemIssuePolicy::respond + ItemIssueService::recordMemberResponse: anything
 * not RESOLVED, and OPEN only once an answer exists).
 *
 * @param {{ status?: string, memberResponse?: string|null }|null|undefined} issue
 */
export function isIssueAnswerable(issue) {
  if (!issue) return false;
  if (issue.status === ISSUE_STATUS.AWAITING_MEMBER) return true;
  return issue.status === ISSUE_STATUS.OPEN && !!issue.memberResponse;
}

// The backend's timeline `title` vocabulary — a fixed, small set of stable
// ENGLISH keys (ItemIssueResource::timelineStep). They are NOT display strings:
// each maps to member-facing Arabic copy client-side. `sent-to-you` and
// `you-replied` can repeat (every revision appends another `you-replied`); the
// three terminal titles are mutually exclusive and appear at most once.
export const TIMELINE_TITLE = {
  SENT_TO_YOU: 'sent-to-you',
  YOU_REPLIED: 'you-replied',
  LEADER_COLLECTED: 'leader-collected',
  MOVED_TO_NEXT_PLAN: 'moved-to-next-plan',
  MARKED_OUT_OF_STOCK: 'marked-out-of-stock',
  // The fourth ending (BE→FE 2026-09-05). The shop HAD the product — it just
  // was not her version — so the club closed the line without claiming anything
  // false about the shelf.
  //
  // It must NEVER be folded into MARKED_OUT_OF_STOCK. Those two are OPPOSITE
  // facts about the same shop, and she is the person who told us which one is
  // true; collapsing them would put the club back to saying "out of stock"
  // about a product it was looking at, which is exactly what this ending exists
  // to stop. Like `request-closed` it carries NO comment — the leader's working
  // note never crosses.
  CLOSED_NOT_AS_ORDERED: 'closed-not-as-ordered',
  // A withdrawal. Previously the backend scrubbed this step, so a withdrawn
  // issue lost its last step and read as truncated; it is now a visible,
  // deliberately reason-free closure (BE→FE 2026-07-28, gap G4). It carries
  // only `at` + `actor` — no comment, no reason, no identity.
  REQUEST_CLOSED: 'request-closed',
};

// How the member's CURRENT answer reached us (App\Enums\Commerce\
// MemberResponseChannel). `in_app` is the implicit default: the member's own
// path writes no channel and legacy rows predate the column, so both read as
// in_app — which is what actually happened. Anything else means a leader
// recorded the answer she gave off-platform.
export const RESPONSE_CHANNEL = {
  IN_APP: 'in_app',
  PHONE: 'phone',
  WHATSAPP: 'whatsapp',
  IN_PERSON: 'in_person',
  TELEGRAM: 'telegram',
};

// Only the off-platform channels get a suffix — an in-app reply is the
// unmarked default and must read exactly as it always has.
const CHANNEL_LABEL_KEY = {
  [RESPONSE_CHANNEL.PHONE]: 'collection.timeline.channel.phone',
  [RESPONSE_CHANNEL.WHATSAPP]: 'collection.timeline.channel.whatsapp',
  [RESPONSE_CHANNEL.IN_PERSON]: 'collection.timeline.channel.in_person',
  [RESPONSE_CHANNEL.TELEGRAM]: 'collection.timeline.channel.telegram',
};

/**
 * The i18n key qualifying a reply with the channel it arrived through, or null
 * when the step should render its plain label.
 *
 * Three distinct inputs collapse to null on purpose, per the backend contract:
 * `in_app` is a positive claim she tapped it herself, `null` means UNKNOWN (a
 * superseded revision — the channel describes only the current answer), and an
 * unrecognised value is a vocabulary we don't have copy for. None of them may
 * fall back to a guess.
 *
 * @param {string|null|undefined} channel
 */
export function replyChannelKey(channel) {
  return CHANNEL_LABEL_KEY[channel] ?? null;
}

// actor drives the step's side/styling (the member's own steps read as "mine").
// 'system' is the plan-close automation; it shares the leader's neutral side.
export const TIMELINE_ACTOR = {
  YOU: 'you',
  LEADER: 'leader',
  SYSTEM: 'system',
};

// The titles that END an issue — used to headline a resolved issue with WHAT
// actually happened instead of a generic "done" (the resource exposes no
// `resolution` field, so the terminal step IS the signal).
const TERMINAL_TITLES = [
  TIMELINE_TITLE.LEADER_COLLECTED,
  TIMELINE_TITLE.MOVED_TO_NEXT_PLAN,
  TIMELINE_TITLE.MARKED_OUT_OF_STOCK,
  TIMELINE_TITLE.CLOSED_NOT_AS_ORDERED,
  TIMELINE_TITLE.REQUEST_CLOSED,
];

// Terminal WITHOUT being an outcome — nothing was collected, deferred or lost,
// so the UI can end the story without borrowing the success styling the real
// outcomes carry.
//
// `request-closed` is a withdrawal: she is told neither why nor by whom.
//
// `closed-not-as-ordered` joins it because NOTHING WAS BOUGHT AND NOTHING WAS
// CHARGED, in either direction. There is no refund, because there was never a
// payment — which is why the Arabic copy is banned from استرداد / رجّعنا /
// تعويض, and why this step must not render beside the collected/deferred
// outcomes as though something moved. It is a blameless close, not a loss.
const NEUTRAL_TERMINAL_TITLES = [
  TIMELINE_TITLE.REQUEST_CLOSED,
  TIMELINE_TITLE.CLOSED_NOT_AS_ORDERED,
];

const TIMELINE_TITLE_KEY = {
  [TIMELINE_TITLE.SENT_TO_YOU]: 'collection.timeline.title.sent-to-you',
  [TIMELINE_TITLE.YOU_REPLIED]: 'collection.timeline.title.you-replied',
  [TIMELINE_TITLE.LEADER_COLLECTED]: 'collection.timeline.title.leader-collected',
  [TIMELINE_TITLE.MOVED_TO_NEXT_PLAN]: 'collection.timeline.title.moved-to-next-plan',
  [TIMELINE_TITLE.MARKED_OUT_OF_STOCK]: 'collection.timeline.title.marked-out-of-stock',
  [TIMELINE_TITLE.CLOSED_NOT_AS_ORDERED]: 'collection.timeline.title.closed-not-as-ordered',
  [TIMELINE_TITLE.REQUEST_CLOSED]: 'collection.timeline.title.request-closed',
};

// An unrecognised title falls back to a deliberately vague "there was an update"
// rather than being DROPPED: the vocabulary is additive on the backend, and a
// silently-swallowed step would leave a hole in a history the member is told is
// complete. Vague-but-present beats accurate-but-missing here.
const TIMELINE_TITLE_FALLBACK_KEY = 'collection.timeline.title.update';

/**
 * Normalize the resource's `timeline` array into render-ready steps: resolves the
 * i18n key for each fixed title, normalizes the actor, and drops malformed
 * entries. Pure — the component owns date formatting (locale-dependent).
 *
 * @param {Array<{ at?: string, actor?: string, title?: string, comment?: string, channel?: string|null }>} timeline
 * @returns {Array<{ at: string|null, actor: string, title: string, titleKey: string, comment: string|null, channelKey: string|null, isTerminal: boolean, isNeutralTerminal: boolean }>}
 */
export function timelineSteps(timeline = []) {
  if (!Array.isArray(timeline)) return [];

  return timeline
    .filter((step) => step && typeof step.title === 'string' && step.title !== '')
    .map((step) => ({
      at: step.at ?? null,
      actor: Object.values(TIMELINE_ACTOR).includes(step.actor)
        ? step.actor
        : TIMELINE_ACTOR.LEADER,
      title: step.title,
      titleKey: TIMELINE_TITLE_KEY[step.title] ?? TIMELINE_TITLE_FALLBACK_KEY,
      // Present ONLY on a `you-replied` step that carried a note — the backend
      // never attaches a comment to any other step.
      comment: typeof step.comment === 'string' && step.comment !== '' ? step.comment : null,
      // Only a reply step carries a channel, and only the LATEST one carries a
      // value — earlier revisions are explicitly null rather than guessed at.
      channelKey: replyChannelKey(step.channel),
      isTerminal: TERMINAL_TITLES.includes(step.title),
      isNeutralTerminal: NEUTRAL_TERMINAL_TITLES.includes(step.title),
    }));
}

/**
 * The i18n key describing HOW a resolved issue ended, read off its terminal
 * timeline step. Returns null only when the timeline carries no terminal step
 * at all, so callers fall back to generic "this one's done" copy.
 *
 * Since 2026-07-28 a withdrawal is no longer scrubbed backend-side: it arrives
 * as `request-closed` and is terminal, so a withdrawn issue now names its own
 * ending instead of falling through to the generic line.
 *
 * @param {Array} timeline  the raw resource timeline
 */
export function terminalOutcomeKey(timeline = []) {
  const terminal = timelineSteps(timeline).find((step) => step.isTerminal);
  return terminal ? terminal.titleKey : null;
}

/**
 * Does the resolved issue's terminal step carry no outcome (a `request-closed`
 * withdrawal) rather than a real one (collected / moved-on / out-of-stock)?
 * Callers that render `terminalOutcomeKey` with success styling (a checkmark,
 * green tint) MUST gate that styling on this being false — see
 * `NEUTRAL_TERMINAL_TITLES` above for why. Mirrors `terminalOutcomeKey`'s null
 * contract: false (not null) when there is no terminal step at all, since
 * "no outcome" and "no *neutral* outcome" both render nothing.
 *
 * @param {Array} timeline  the raw resource timeline
 */
export function terminalOutcomeIsNeutral(timeline = []) {
  const terminal = timelineSteps(timeline).find((step) => step.isTerminal);
  return terminal ? terminal.isNeutralTerminal : false;
}

// TurnPill variant → its label key. Lives here (not inside TurnPill) because the
// page also needs the label as an aria-label for the focus target it moves into,
// and two copies of this map would drift.
export const PILL_LABEL_KEY = {
  member: 'collection.pill.member',
  resolved: 'collection.pill.resolved',
  handled: 'collection.state.beingHandled',
  oos: 'collection.state.outOfStock',
};

/** The label key for a TurnPill variant, falling back to the member variant. */
export function pillLabelKey(variant) {
  return PILL_LABEL_KEY[variant] ?? PILL_LABEL_KEY.member;
}

// Reason → a consistent Framework7 icon (member + leader iconography agree:
// variant=color, quality=photo, supplier=shop, price=tag — UX spec Section Additional
// Patterns). All are valid f7 glyph names (verified in framework7-icons).
export const REASON_ICON = {
  [ISSUE_REASON.VARIANT_UNAVAILABLE]: 'paintbrush_fill',
  [ISSUE_REASON.QUALITY_CONCERN]: 'camera_fill',
  [ISSUE_REASON.SUPPLIER_OOS]: 'building_2_fill',
  [ISSUE_REASON.PRICE_CHANGE]: 'tag_fill',
  [ISSUE_REASON.SHORTFALL]: 'tray_fill',
  [ISSUE_REASON.OTHER]: 'info_circle_fill',
};

export function reasonIcon(reason) {
  return REASON_ICON[reason] || REASON_ICON[ISSUE_REASON.OTHER];
}

// Reason → a SHORT (1-3 word) label for the dense decisions-hub list row —
// distinct from `collection.reason.*`, which are full sentences for the
// detail card. Authored via poolit-voice (2026-07-26). Falls back to the
// 'other' label for an unrecognised reason, mirroring reasonIcon.
export const REASON_LABEL_KEY = {
  [ISSUE_REASON.VARIANT_UNAVAILABLE]: 'collection.reasonLabel.variant_unavailable',
  [ISSUE_REASON.QUALITY_CONCERN]: 'collection.reasonLabel.quality_concern',
  [ISSUE_REASON.SUPPLIER_OOS]: 'collection.reasonLabel.supplier_oos',
  [ISSUE_REASON.PRICE_CHANGE]: 'collection.reasonLabel.price_change',
  [ISSUE_REASON.SHORTFALL]: 'collection.reasonLabel.shortfall',
  [ISSUE_REASON.OTHER]: 'collection.reasonLabel.other',
};

export function reasonLabelKey(reason) {
  return REASON_LABEL_KEY[reason] || REASON_LABEL_KEY[ISSUE_REASON.OTHER];
}

// ── The answer controls ──────────────────────────────────────────────────────
//
// One control per thing the member can tap, derived from the CONTRACT and
// nothing else. Two rules, both load-bearing:
//
//  1. Render exactly `allowedResponses` — no more, no fewer. The backend's
//     `ItemIssue::availableResponses()` is the same predicate its write gate
//     uses, so the buttons shown and the answers accepted cannot drift apart.
//     A response missing from the list is a 422, and one we hide is an answer
//     the member is silently denied.
//  2. NEVER derive the set from `reason`. `reason` still ships, but it is a
//     reporting tag now — the member reads `question`. The per-reason response
//     matrix is gone with the six-case enum, and re-deriving one here would
//     resurrect exactly the coupling this redesign removed.
//
// YES expands to ONE control per offered option (the option IS the control —
// tapping "navy" both answers yes and picks navy, in one tap, with no separate
// picker to satisfy first). A question with no options gets a single solo YES.
export const RESPONSE_META = {
  [MEMBER_RESPONSE.YES]: {
    // The solo-YES label. An option control is labelled by the leader's own
    // `option.label` instead, which is free text and never an i18n key.
    labelKey: 'collection.answer.yesBare.label',
    kind: 'yes',
  },
  [MEMBER_RESPONSE.NO]: {
    labelKey: 'collection.response.decline',
    kind: 'no',
  },
  [MEMBER_RESPONSE.WAIT]: {
    // Reuses the shipped `defer` copy verbatim — same promise, same words.
    labelKey: 'collection.response.defer',
    kind: 'wait',
  },
};

// A question about goods ALREADY IN HER BOX needs different words for the same
// two answers: "go ahead" and "no thanks" both presuppose a collection that has
// already happened. The response VALUES are unchanged — only the label moves.
const COLLECTED_LABEL_KEY = {
  [MEMBER_RESPONSE.YES]: 'collection.answer.collected.yes',
  [MEMBER_RESPONSE.NO]: 'collection.answer.collected.no',
};

/**
 * A collected card answers with YES and NO **only** — no option controls, no
 * WAIT. This is the second deliberate departure from "render exactly
 * `allowedResponses`", and each half has its own reason:
 *
 *  • **Options.** `ItemIssueService::collect()` returns at its COLLECTED branch
 *    BEFORE any option is applied — a price option changes nothing and a qty
 *    option throws — so «آجيبلك 2 بس بنفس السعر» on goods already in her carton
 *    is a promise the backend structurally cannot honour through that path.
 *  • **WAIT.** «تأجيل للتجميع الجاي» offers to postpone something she already
 *    has.
 *
 * (The FIRST departure — suppressing NO because `answer.collected.no` was
 * unwritten — is closed. It cost her the only way to record that something was
 * wrong with what arrived, leaving YES as her entire answer set. Owner decision
 * D1, 2026-08-11: her answer is recorded and the admin sees it — no promise of
 * contact, return, refund or removal, because none of those exist as a process.
 * `poolit-voice` wrote the label to say exactly that and no more.)
 */
const COLLECTED_SUPPRESSED = new Set([MEMBER_RESPONSE.WAIT]);

// Render order, independent of the order the backend happens to list them in:
// the option-YESes first (in contract order — the admin's own ranking), then
// NO, then WAIT. Affirmative before negative before postpone, always.
const KIND_ORDER = { yes: 0, no: 1, wait: 2 };

/**
 * The answer controls for one question, in render order.
 *
 * @param {string[]} allowedResponses the contract's list, rendered verbatim
 * @param {Array<{index:number,label:string,priceCents:number|null,qty:number|null}>} [options]
 *   the leader's offered options; each becomes its own YES control
 * @param {{ isCollected?: boolean }} [opts] isCollected swaps the YES/NO labels
 *   for the already-in-her-box wording (see COLLECTED_LABEL_KEY)
 * @returns {Array<{ key: string, response: string, kind: string,
 *   optionIndex: number|null, option: object|null, labelKey: string|null }>}
 *   `labelKey` is null on an option control — `option.label` is the text, and it
 *   is the leader's free words, which may name something the catalogue has never
 *   recorded. Do not try to resolve it against the catalogue.
 */
export function orderedResponses(allowedResponses = [], options = [], { isCollected = false } = {}) {
  if (!Array.isArray(allowedResponses)) return [];
  const offered = Array.isArray(options) ? options : [];

  const controls = [];

  for (const response of allowedResponses) {
    const meta = RESPONSE_META[response];
    // Defensive: an unrecognised value is a vocabulary we have no control for.
    // Dropping it is safer than rendering a button whose effect we can't name.
    if (!meta) continue;
    // …and the same reasoning for a control that cannot be honoured on goods
    // already in her carton (see COLLECTED_SUPPRESSED).
    if (isCollected && COLLECTED_SUPPRESSED.has(response)) continue;

    const labelKey =
      (isCollected && COLLECTED_LABEL_KEY[response]) || meta.labelKey;

    // Options are suppressed on a collected card for the same reason as WAIT —
    // `collect()` never reaches the code that would apply one — so YES renders
    // as the bare "it's fine, keep it" control rather than one control per
    // alternative the admin can no longer carry out.
    if (response === MEMBER_RESPONSE.YES && offered.length > 0 && !isCollected) {
      offered.forEach((option, position) => {
        const optionIndex = Number.isInteger(option?.index) ? option.index : position;
        controls.push({
          key: `yes:${optionIndex}`,
          response,
          kind: meta.kind,
          optionIndex,
          option,
          labelKey: null,
        });
      });
      continue;
    }

    controls.push({
      key: response,
      response,
      kind: meta.kind,
      optionIndex: null,
      option: null,
      labelKey,
    });
  }

  return controls.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
}

/**
 * The i18n key naming ONE response — for the "your previous answer" strip, so a
 * recorded answer is named with the exact words that were on the control the
 * member tapped. Returns null for an unknown response.
 *
 * An answer that carried an option is named by that option's own label, not by
 * a key, so the caller must check `memberOptionIndex` first; this only ever
 * names the bare answer.
 *
 * It names an answer ALREADY ON RECORD — reachable on a collected item via an
 * admin relay, or by the item being collected after she answered — so it uses
 * the collected wording whenever one exists, INCLUDING for NO. That last part
 * was a real defect while `answer.collected.no` was unwritten: a relayed NO on
 * goods in her carton recapped as «إلغاء الصنف», telling her the item was
 * cancelled, which is the one claim this surface must never make.
 *
 * @param {string} response
 * @param {{ isCollected?: boolean }} [opts]
 */
export function responseLabelKey(response, { isCollected = false } = {}) {
  if (!RESPONSE_META[response]) return null;
  if (isCollected && COLLECTED_LABEL_KEY[response]) return COLLECTED_LABEL_KEY[response];
  return RESPONSE_META[response].labelKey;
}

// NO's money line — the only consequence subline left on a control. Everything
// the three answers used to promise individually is now said once, card-level,
// by `answer.advice`: her answer reaches the leader, who will do their best to
// follow it. Repeating a per-control promise beside that reads as two different
// commitments.
const DECLINE_MONEY_OUTCOME_KEY = 'collection.outcome.declineAmount';

/**
 * The i18n key for a control's stated consequence, or null for a deliberately
 * silent control.
 *
 * Only NO has one, and only when a figure is actually in hand: the key carries
 * an `{amount}` slot that would otherwise render raw, and — per this feature's
 * standing money rule — a figure that cannot be known is OMITTED, never rendered
 * as zero. On an already-collected item there is no such figure at all (see
 * `declineLineImpact`), so `hasAmount` is false there and this correctly falls
 * silent rather than claiming the line comes off a box it is already in.
 *
 * @param {string} response
 * @param {{ hasAmount?: boolean }} [opts] pass hasAmount ONLY when a derivable
 *   figure exists for this exact control
 */
export function responseOutcomeKey(response, { hasAmount = false } = {}) {
  if (!RESPONSE_META[response]) return null;
  if (response === MEMBER_RESPONSE.NO && hasAmount) return DECLINE_MONEY_OUTCOME_KEY;
  return null;
}
